import { Component, Inject} from '@angular/core';
import { UserService } from '../../services/user.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import {} from '@aws-amplify/ui-angular';
import { HttpClient } from '@angular/common/http';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import { VariablesComplianceService } from '../../services/variables-compliance.service';
import { ComentarioCompliance } from '../../interfaces/comentario-compliance';
import { EstadoCompliance } from '../../interfaces/estado-compliance';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { PagadorService } from '../../services/pagador.service';
import { Pagador } from '../../interfaces/pagador';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { RejectCompliancePagadorComponent } from './reject-compliance-pagador/reject-compliance-pagador.component';
import { KycApiService, PepChileCheckResult } from 'src/app/auth/services/kyc-api.service';
import { firstValueFrom } from 'rxjs';
import { DepositosRetenidosService } from '../../services/depositos-retenidos.service';
import { DepositosRetenidosDialogComponent } from '../depositos-retenidos-dialog/depositos-retenidos-dialog.component';

export interface Image {
  key: string;
  url: string;
}
@Component({
  selector: 'app-pagador-compliance',
  templateUrl: './pagador-compliance.component.html',
  styleUrls: ['./pagador-compliance.component.sass']
})
export class PagadorComplianceComponent {
  images: Image[] = [];
  updateParent= false;
  id: string = '';
  user: any;
  userEmail: string = '';
  fotoIdFrontal: string = '';
  fotoIdPosterior: string = '';
  selfie: string = '';
  estatuto: string = '';
  erut: string = '';
  documentoIdTributario: string = '';
  mostarFrontal: boolean = true;
  comentariosCompliance: ComentarioCompliance[] = [];
  estadosCompliance: EstadoCompliance[] = [];
  formCompliance: FormGroup | undefined;
  estadoComplianceBase: Object = {};
  pagador: Pagador | undefined;

  // Validación PEP Chile (RegCheq) — se corre al abrir, con el token del
  // operador logueado (el endpoint PEP exige grupo aguacate_operator).
  pepChileResult: PepChileCheckResult | null = null;
  pepChileLoading = false;
  pepChileError: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private spinner: SpinnerService,
    private fb: FormBuilder,
    private varCompliance: VariablesComplianceService,
    private http: HttpClient,
    private cognito: CognitoService,
    private userService: UserService,
    private pagadorService: PagadorService,
    private dialog: MatDialog,
    private reference: MatDialogRef<PagadorComplianceComponent>,
    private s3UrlService: S3UrlService,
    private kycApiService: KycApiService,
    private depositosRetenidos: DepositosRetenidosService,
  ) {
    this.pagador = data.pagador;
    this.loadUserEmail();
    this.getVariablesCompliance();
    this.loadPagador();
    // El PEP no se corre en el registro por link (no hay operador); se valida
    // acá, al abrir el compliance, y es obligatorio antes de aprobar (CL).
    this.checkPepChile();
  }

  /** CL = documento RUT. El PEP de RegCheq solo aplica a Chile. */
  get esCL(): boolean {
    return this.pagador?.tipoId === 'RUT';
  }

  /**
   * P3: la selfie entró por el respaldo de archivo del registro por link (en un
   * computador, puede ser una foto de la galería y no una tomada en el momento).
   */
  get selfieDesdeArchivo(): boolean {
    const log = (this.pagador as any)?.log;
    return Array.isArray(log) && log.some((e: any) => e?.selfieDesdeArchivo === true);
  }
  
  /** Entrada del log escrita al crear el pagador desde el link permanente (12/10/2026). */
  private get entradaLinkPermanente(): any {
    const log = (this.pagador as any)?.log;
    return Array.isArray(log) ? log.find((e: any) => e?.origen === 'link-permanente') : null;
  }

  get registroPorLinkPermanente(): boolean {
    return !!this.entradaLinkPermanente;
  }

  /** Marcas del registro (WhatsApp/email del dueño, contacto compartido, misma conexión). */
  get alertasLink(): string[] {
    const alertas = this.entradaLinkPermanente?.alertas;
    return Array.isArray(alertas) ? alertas.filter((a: unknown) => typeof a === 'string') : [];
  }

  get emailVerificadoLink(): string | null {
    return this.entradaLinkPermanente?.emailVerificado ?? null;
  }

  async refreshPagador(){
    this.pagador = await this.pagadorService.getPagador(this.pagador?.pagadorId ?? '');
  }

  /**
   * Valida el PEP Chile contra RegCheq (usa el token del operador logueado).
   * Igual que el compliance de usuarios: se corre al abrir el modal. Si figura
   * como PEP, marca el flag para que el operador lo tenga en cuenta al decidir.
   */
  async checkPepChile() {
    if (!this.pagador?.numeroId || !this.esCL) return;

    this.pepChileLoading = true;
    this.pepChileError = null;
    try {
      const apellidos = (this.pagador.apellidos || '').trim();
      const parts = apellidos.split(/\s+/);
      const fatherName = parts[0] || undefined;
      const motherName = parts.length > 1 ? parts.slice(1).join(' ') : undefined;

      this.pepChileResult = await this.kycApiService.checkPepChile({
        dni: this.pagador.numeroId,
        personType: this.pagador.tipoUsuarioId === 1 ? 'legal' : 'natural',
        name: this.pagador.nombre || undefined,
        fatherName,
        motherName,
        nationality: this.pagador.nacionalidad || undefined,
        country: this.pagador.paisResidencia || undefined,
      });

      if (this.pepChileResult?.isPep) {
        this.pagador.esPoliticamenteExpuesto = true;
        this.formCompliance?.patchValue({ politicamenteExpuesto: true });
        this.formCompliance?.get('politicamenteExpuesto')?.markAsTouched();
      }
    } catch (error: any) {
      this.pepChileResult = null;
      this.pepChileError =
        error?.message || 'Error al consultar PEP Chile. Intente nuevamente.';
    } finally {
      this.pepChileLoading = false;
    }
  }

  loadPagador() {
    this.getIdImages();
    // Los checks quedan editables siempre (P2): antes, con fuente 2 (RegCheq) se
    // deshabilitaban y el operador no podía corregir lo que traía el proveedor.
    this.formCompliance = this.fb.group({
      estadoCompliance: new FormControl(this.pagador?.estadoComplianceID, [
        Validators.required,
      ]),
      comentarioCompliance: new FormControl(
        this.pagador?.comentarioComplianceID,
        [Validators.required]
      ),
      esListaONU: new FormControl(this.pagador?.esListaONU, [Validators.required]),
      politicamenteExpuesto: new FormControl(this.pagador?.esPoliticamenteExpuesto, [
        Validators.required,
      ]),
      esRTP: new FormControl(this.pagador?.esRTP, [Validators.required]),
    });
  }
  async loadUserEmail() {
    const userInSession = await this.cognito.getUser();
    this.userEmail = userInSession.attributes.email;
  }

  async getVariablesCompliance() {
    [this.comentariosCompliance, this.estadosCompliance] = await Promise.all([
      this.varCompliance.getComentariosCompliance(),
      this.varCompliance.getEstadosCompliance(),
    ]);
  }

  close(){
    this.reference.close(this.updateParent);
  }

  /**
   * Post-VULN-01: backend firma cada URL. Foto frontal/posterior se
   * proyectan a `data:` inline (legacy); estatuto/e_rut/documentoId van
   * directo como signed URL para visor PDF.
   */
  async getIdImages() {
    const loadAsDataUrl = async (
      key: string | null | undefined,
      target: 'fotoIdFrontal' | 'fotoIdPosterior' | 'selfie',
    ): Promise<void> => {
      if (!key) return;
      const signedUrl = await this.s3UrlService.getReadUrl(key);
      if (!signedUrl) return;
      this.http
        .get(signedUrl, { responseType: 'blob' })
        .subscribe((response) => {
          const reader = new FileReader();
          reader.readAsDataURL(response);
          reader.onloadend = () => {
            (this as any)[target] = reader.result as string;
          };
        });
    };

    await Promise.all([
      loadAsDataUrl(this.pagador?.fotoIdfrontal, 'fotoIdFrontal'),
      loadAsDataUrl(this.pagador?.fotoIdPosterior, 'fotoIdPosterior'),
      loadAsDataUrl(this.pagador?.selfie, 'selfie'),
    ]);

    if (this.pagador?.estatuto) {
      this.estatuto =
        (await this.s3UrlService.getReadUrl(this.pagador.estatuto)) ?? '';
    }
    if (this.pagador?.e_rut) {
      this.erut =
        (await this.s3UrlService.getReadUrl(this.pagador.e_rut)) ?? '';
    }
    if (this.pagador?.documentoId) {
      this.documentoIdTributario =
        (await this.s3UrlService.getReadUrl(this.pagador.documentoId)) ?? '';
    }
  }
  async ngOnInit() {}

  async fileCheckType(file: File) {
    let isValid = false;
    const isPNG = this.check([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    const isJPEG = this.check([0xff, 0xd8, 0xff]);
    const buffers: Buffer = await this.readBuffer(file, 0, 8);
    const uint8Array = new Uint8Array(buffers);
    if (isPNG(uint8Array)) {
      isValid = true;
    }
    if (isJPEG(uint8Array)) {
      isValid = true;
    }
    return isValid;
  }

  readBuffer(file: File, start = 0, end = 2): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file.slice(start, end));
    });
  }

  check(headers: any) {
    return (buffers: any, options = { offset: 0 }) =>
      headers.every(
        (header: any, index: any) => header === buffers[options.offset + index]
      );
  }

  downloadPDF(url: string) {
    window.open(url, '_blank');
  }

  guardarCompliance() {
    if (this.formCompliance?.invalid || !this.formCompliance?.touched) {
      alert('agrega algo');
      return;
    }
    const ref = this.spinner.start();
    this.pagadorService
      .updateUserChecks(
        this.pagador?.pagadorId ?? '',
        this.userEmail,
        this.formCompliance.value
      )
      .subscribe(
        (res) => {
        //  this.loadUser();
          this.spinner.stop(ref);
          this.refreshPagador();
          this.updateParent = true;
        },
        (err) => this.spinner.stop(ref)
      );
  }

  async aproveCompliance(){
    // Obligatorio (CL): el PEP debe estar validado antes de aprobar.
    if (this.esCL && !this.pepChileResult) {
      await Swal.fire({
        icon: 'warning',
        title: 'Falta validar el PEP',
        text: this.pepChileLoading
          ? 'La validación de PEP está en curso, espera un momento y reintenta.'
          : 'Debes validar el PEP (Chile) antes de aprobar. Usa "Revalidar PEP".',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }
    // Los checks que valen son los del formulario: el operador pudo corregirlos,
    // o el PEP de RegCheq pudo marcarlos al abrir el modal.
    const checks = this.formCompliance?.getRawValue() ?? {};
    // Advertencia si hay coincidencias de compliance (RegCheq / sanciones).
    const flags: string[] = [];
    if (checks.politicamenteExpuesto) flags.push('Persona Políticamente Expuesta (PEP)');
    if (checks.esListaONU) flags.push('Listas internacionales / ONU');
    if (checks.esRTP) flags.push('RTP');
    if (flags.length) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: '⚠️ Coincidencias de compliance',
        html:
          `Este pagador presenta coincidencias:<br><b>${flags.join('<br>')}</b>` +
          `<br><br>¿Deseas aprobarlo de todos modos?`,
        showCancelButton: true,
        confirmButtonText: 'Aprobar igual',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#c0392b',
        cancelButtonColor: '#6b7280',
      });
      if (!confirm.isConfirmed) return;
    }
    const pagadorId = this.pagador?.pagadorId ?? '';
    const ref = this.spinner.start();
    try {
      // P2: los checks (PEP, RTP, ONU) se guardan junto con la aprobación. Antes
      // solo se guardaban con "Guardar", y aprobar dejaba el PEP sin registrar.
      await firstValueFrom(this.pagadorService.updateUserChecks(pagadorId, this.userEmail, checks));
      await firstValueFrom(
        this.pagadorService.updateUserCompliance(pagadorId, this.userEmail, {
          comentarioCompliance: 0,
          estadoCompliance: 3,
        }),
      );
    } catch (err) {
      // Ej.: desbloquear un pagador cuyo documento ya es de un cliente.
      this.spinner.stop(ref);
      this.mostrarError(err, 'No se pudo aprobar el pagador');
      return;
    }
    this.spinner.stop(ref);
    this.updateParent = true;
    await this.refreshPagador();
    await Swal.fire({
      icon: 'success',
      title: 'Compliance actualizado',
      text: 'Se ha aprobado el pagador con éxito',
      showCancelButton: false,
      showDenyButton: false,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#1e5a3f',
    });
    await this.ofrecerDepositosRetenidos();
  }

  /**
   * A1: al aprobar, ofrece asignar los depósitos que quedaron retenidos
   * mientras el pagador no estaba aprobado (desde que se creó). Si no hay, no
   * muestra nada; si la búsqueda falla, siguen en "Transacciones sin asignar".
   */
  private async ofrecerDepositosRetenidos(): Promise<void> {
    const p: any = this.pagador;
    if (!p?.usuarioId) return;
    try {
      const documento = Number(p.tipoUsuarioId) === 1 ? p.rutEmpresa : p.numeroId;
      const depositos = await this.depositosRetenidos.buscar(documento, p.created_at);
      if (!depositos.length) return;
      const dialogo = this.dialog.open(DepositosRetenidosDialogComponent, {
        width: '760px',
        maxWidth: '95vw',
        data: {
          titulo: 'Depósitos retenidos de este pagador',
          usuarioIdDestino: p.usuarioId,
          destino: `la cuenta del cliente #${p.usuarioId}`,
          depositos,
        },
      });
      await firstValueFrom(dialogo.afterClosed());
    } catch {
      // La aprobación ya quedó hecha.
    }
  }

  /**
   * P2: da de baja el pagador (`activo = 0`). Deja de recibir abonos y
   * desaparece de "Mis pagadores" del cliente, como cuando él lo elimina. A
   * diferencia de Bloquear, el dueño ya no lo ve.
   */
  async darDeBaja(): Promise<void> {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Dar de baja este pagador?',
      html:
        'Deja de recibir abonos y <b>desaparece de "Mis pagadores"</b> del cliente, como si él lo eliminara. ' +
        'Si prefieres que el cliente lo siga viendo como "Bloqueado", usa Bloquear.',
      showCancelButton: true,
      confirmButtonText: 'Dar de baja',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#374151',
      cancelButtonColor: '#6b7280',
    });
    if (!confirmacion.isConfirmed) return;
    const ref = this.spinner.start();
    try {
      await this.pagadorService.inactivatePagador(this.pagador?.pagadorId ?? '');
      this.spinner.stop(ref);
      Swal.fire({
        icon: 'success',
        title: 'Pagador dado de baja',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#1e5a3f',
      });
      this.updateParent = true;
      this.refreshPagador();
    } catch (err) {
      this.spinner.stop(ref);
      this.mostrarError(err, 'No se pudo dar de baja el pagador');
    }
  }

  /**
   * Bloquea el pagador (estado de compliance 4) en Nest: no se borra, queda
   * con log de quién y por qué, y deja de recibir abonos.
   */
  async bloquearPagador(): Promise<void> {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Bloquear este pagador?',
      html:
        'El pagador <b>deja de recibir abonos</b> y su documento deja de contar en las validaciones de RUT. ' +
        'No se borra: queda en el historial y se puede desbloquear aprobándolo si su documento sigue libre.',
      input: 'text',
      inputLabel: 'Motivo (opcional, queda en el historial)',
      inputAttributes: { maxlength: '300' },
      showCancelButton: true,
      confirmButtonText: 'Bloquear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6d28d9',
      cancelButtonColor: '#6b7280',
    });
    if (!confirmacion.isConfirmed) return;
    const ref = this.spinner.start();
    try {
      const res = await this.pagadorService.bloquearPagador(
        this.pagador?.pagadorId ?? '',
        (confirmacion.value || '').toString().trim() || undefined,
      );
      this.spinner.stop(ref);
      Swal.fire({
        icon: 'success',
        title: res?.yaEstabaBloqueado ? 'El pagador ya estaba bloqueado' : 'Pagador bloqueado',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#1e5a3f',
      });
      this.updateParent = true;
      this.refreshPagador();
    } catch (err) {
      this.spinner.stop(ref);
      this.mostrarError(err, 'No se pudo bloquear el pagador');
    }
  }

  /** Muestra el mensaje del backend (400/404/409) o uno genérico. */
  private mostrarError(err: any, titulo: string): void {
    const mensaje =
      (typeof err?.error?.message === 'string' && err.error.message) ||
      (typeof err === 'string' && err) ||
      'Intenta nuevamente.';
    Swal.fire({
      icon: 'error',
      title: titulo,
      text: mensaje,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#1e5a3f',
    });
  }

  async backlistUser(){
    // P2: antes se aplicaba con un clic, sin confirmar.
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Pasar este pagador a BLACKLIST?',
      text: 'Deja de recibir abonos y queda marcado en blacklist para compliance.',
      showCancelButton: true,
      confirmButtonText: 'Pasar a blacklist',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });
    if (!confirmacion.isConfirmed) return;
    const ref = this.spinner.start();
    this.pagadorService
    .updateUserCompliance(
      this.pagador?.pagadorId ?? '',
      this.userEmail,
     {comentarioCompliance: 0,
     estadoCompliance: 2}
    )
    .subscribe(
      (res) => {
        //this.loadUser();
        Swal.fire({
          icon: 'success',
          title: 'Compliance actualizado',
          text: 'Se ha marcado el usuario como blacklist',
          showCancelButton: false,
          showDenyButton: false,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1e5a3f',

        });
        this.updateParent = true;
        this.spinner.stop(ref);
        this.refreshPagador();
      },
      (err) => {
        this.spinner.stop(ref);
        this.mostrarError(err, 'No se pudo marcar como blacklist');
      }
    );
  }
  rejectUser(){
   this.dialog.open(RejectCompliancePagadorComponent, {
    data: {
      userEmail: this.userEmail,
      pagadorId: this.pagador?.pagadorId,
      comentariosCompliance: this.comentariosCompliance
    }
   }).afterClosed().subscribe(res=> {
    if(res === true){
      this.updateParent = true;
      this.refreshPagador();
     // this.loadUser();
    }
   })
  }

}
